"use client";

import { useState } from "react";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconBlog from "@/app/(components)/generics/Icons/IconBlog";
import IconDocuments from "@/app/(components)/generics/Icons/IconDocuments";
import IconInfo from "@/app/(components)/generics/Icons/IconInfo";
import IconQuestionMarkRounded from "@/app/(components)/generics/Icons/IconQuestionMarkRounded";
import Popover from "@/app/(components)/generics/Popover/Popover";
import PopoverContent from "@/app/(components)/generics/Popover/PopoverContent";
import PopoverTrigger from "@/app/(components)/generics/Popover/PopoverTrigger";
import { Tooltip, TooltipPosition, TooltipType } from "@/app/(components)/generics/Tootlip/Tooltip";
import { useBrand } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { SidebarStore } from "@/app/stores/SidebarStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { URLs } from "@/app/utils/URLs";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { Routes } from "@/app/vars/Routes";
import sidebarStyles from "../../Sidebar.module.css";
import styles from "./SidebarHelp.module.css";

type Props = {
  lang: string;
  isOpen: boolean;
};

export default function SidebarHelp(props: Props) {
  const T = useT();
  const { lang, isOpen } = props;

  const [isHelpPopoverVisible, setIsHelpPopoverVisible] = useState(false);
  const { closeSidebarOnMobile } = SidebarStore();

  // A company member never connects the LinkedIn extension nor an AI client (MCP), so their
  // guides are hidden from the help menu too.
  const isTenant = useBrand() !== null;

  return (
    <div className={styles.helpSection}>
      <Popover
        isVisible={isHelpPopoverVisible}
        setIsVisible={setIsHelpPopoverVisible}
        openAfterClick={true}
        closeWhenClickOutside={true}
        className={styles.helpPopover}
        isPortaled={isOpen}
      >
        <PopoverTrigger className={sidebarStyles.navItem}>
          {isOpen ? (
            <Button
              min={false}
              params={{ lang }}
              text={T(TKey.HELP_SUPPORT)}
              iconOnly={false}
              beforeIcon={<IconQuestionMarkRounded />}
              type={ButtonType.GHOST}
              full={true}
              isLink={false}
              className={UStyles.classNames(
                sidebarStyles.navButton,
                isHelpPopoverVisible ? sidebarStyles.activeItem : "",
              )}
            />
          ) : (
            <Tooltip
              position={TooltipPosition.RIGHT}
              gap={Limits.SIDEBAR_TOOLTIP_GAP}
              type={TooltipType.GREEN}
              content={T(TKey.HELP_SUPPORT)}
              className={sidebarStyles.fullWidth}
              disabled={isHelpPopoverVisible}
              trigger={
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    min={false}
                    params={{ lang }}
                    iconOnly={true}
                    beforeIcon={<IconQuestionMarkRounded />}
                    type={ButtonType.GHOST}
                    full={false}
                    isLink={false}
                    className={UStyles.classNames(
                      sidebarStyles.navButton,
                      isHelpPopoverVisible ? sidebarStyles.activeItem : "",
                    )}
                    ariaLabel={T(TKey.HELP_SUPPORT)}
                  />
                </div>
              }
            />
          )}
        </PopoverTrigger>
        <PopoverContent
          position={
            isOpen
              ? { default: { bottom: "100%", left: "0" } }
              : {
                  default: {
                    bottom: "0",
                    left: `calc(100% + ${Limits.SIDEBAR_TOOLTIP_GAP}px)`,
                  },
                }
          }
          className={UStyles.classNames(
            styles.helpPopoverContent,
            !isOpen ? sidebarStyles.collapsedPopover : "",
          )}
        >
          {!isTenant && (
            <>
              <span className={styles.categoryLabel}>{T(TKey.HELP_CATEGORY_RESOURCES)}</span>

              <Button
                min={false}
                params={{ lang }}
                text={T(TKey.BLOG_MENU_LABEL)}
                href={URLs.pathOf(Routes.BLOG)}
                isLink={true}
                type={ButtonType.GHOST}
                full={true}
                beforeIcon={<IconBlog />}
                className={styles.helpLink}
                onClick={closeSidebarOnMobile}
              />
            </>
          )}

          <span className={styles.categoryLabel}>{T(TKey.HELP_CATEGORY_LEGAL)}</span>

          <Button
            min={false}
            params={{ lang }}
            text={T(TKey.TERMS)}
            href={URLs.pathOf(Routes.TERMS)}
            isLink={true}
            type={ButtonType.GHOST}
            full={true}
            beforeIcon={<IconDocuments />}
            className={styles.helpLink}
            onClick={closeSidebarOnMobile}
          />

          <Button
            min={false}
            params={{ lang }}
            text={T(TKey.PRIVACY)}
            href={URLs.pathOf(Routes.PRIVACY)}
            isLink={true}
            type={ButtonType.GHOST}
            full={true}
            beforeIcon={<IconInfo />}
            className={styles.helpLink}
            onClick={closeSidebarOnMobile}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
